import { z } from "zod";
import { marketSchema } from "./market.js";
/* The questionnaire engine: instrument definitions and the answer wire shape.
 *
 * The infusion pre-appointment screener and the wellbeing questionnaire are
 * the same comprehensive questionnaire by design, not two content sets — one
 * engine, one shared instrument (platform/docs/infusion-journey.md).
 * Instrument *definitions* — which keys exist, their kind, whether they're
 * required — live here as a versioned constant, reviewed on change like the
 * analytics catalog, rather than in a DB table. `InstrumentName` stays a
 * generic keyed type rather than a hardcoded literal in case a genuinely
 * distinct instrument shows up later. The DB (032) stores answers typed
 * against whatever version a submission points at.
 *
 * completeness() is the only derived value this module produces — answered
 * vs. required, never a score, band or eligibility judgement. Root
 * CLAUDE.md's "the software does not interpret" applies here exactly as it
 * does to a measured lab value; see questionnaires-purity.test.ts.
 */
export const instrumentNameSchema = z.enum(["health_questionnaire"]);
export const questionnaireStatusSchema = z.enum(["not_started", "in_progress", "submitted"]);
export const questionnaireStateSchema = z.object({
    instrument: instrumentNameSchema,
    version: z.number().int().positive(),
    status: questionnaireStatusSchema,
});
// Matches the questionnaire_answers.kind CHECK (032) exactly.
export const answerKindSchema = z.enum(["text", "number", "boolean", "date", "choice", "multi_choice"]);
// Every free-text question in v1 is optional and never blocks a section
// (questionCommonSchema's own `required: false` everywhere), but a
// client-only cap on Art. 9 free text is not a cap -- enforced both in the
// UI's Textarea and here, the shared constant saveAnswers checks against.
// Same precedent as symptoms.ts's SYMPTOM_FREE_TEXT_MAX.
export const FREE_TEXT_MAX = 1000;
// `label` is optional and deliberately left unset in INSTRUMENT_DEFINITIONS —
// option copy lives in @ferrivia/questionnaire-ui, not the contract.
// `markets` limits where an option is offered, for product lists that differ
// by country (iron infusion brands). Unset offers it everywhere. Offering, not
// accepting: an answer naming an option from another market still saves.
export const questionOptionSchema = z.object({
    id: z.string(),
    label: z.string().optional(),
    markets: z.array(marketSchema).optional(),
});
// A pure rendering condition on another question's answer — never a scoring
// input. It decides whether a question is shown, not what a value means.
// Inside a repeating group's field list, `key` resolves against the item's
// own fields, not the submission's answers — a global lookup would silently
// do the wrong thing once two items hold different values.
// `includesAny` exists for the one case in the instrument where the gating
// question itself is multi_choice (contraceptives_details -> other):
// `equals`/`notEquals` compare against a single value and can't express
// "one of the selected options is X".
export const questionShowIfSchema = z
    .object({
    key: z.string(),
    equals: z.union([z.string(), z.boolean()]).optional(),
    notEquals: z.union([z.string(), z.boolean()]).optional(),
    includesAny: z.array(z.string()).optional(),
})
    .refine((showIf) => [showIf.equals, showIf.notEquals, showIf.includesAny].filter((v) => v !== undefined).length === 1, {
    message: "exactly one of equals, notEquals or includesAny must be set",
});
// A single field inside a repeating group's item. Shares kind/options/showIf
// with a top-level question, but "options" only apply to choice/multi_choice
// fields and "showIf" is item-scoped.
export const questionGroupFieldSchema = z.object({
    key: z.string(),
    kind: answerKindSchema,
    required: z.boolean(),
    options: z.array(questionOptionSchema).optional(),
    showIf: questionShowIfSchema.optional(),
    integer: z.boolean().optional(),
    min: z.number().optional(),
    max: z.number().optional(),
    // A rendering hint about this field, not copy -- belongs beside
    // integer/min/max, not in @ferrivia/questionnaire-ui. `supplement_picker`
    // grows `name` a supplement-search-and-select affordance instead of a
    // plain text input; `select` renders a `choice` field as a dropdown
    // instead of chips, for option lists too long to tap through.
    // `photo_upload` renders a `text` field as up to
    // QUESTIONNAIRE_PHOTOS_PER_ITEM photo tiles; its value is the
    // comma-separated ids of questionnaire_photos rows (see
    // questionnaire-photos.ts), validated by the store.
    control: z.enum(["supplement_picker", "select", "photo_upload"]).optional(),
    // A rendering hint for a `supplement_picker` field: which catalog
    // category it searches, so the iron question lists only iron products.
    catalogCategory: z.enum(["iron", "other"]).optional(),
    // True for a field the user never fills in directly -- it is written
    // alongside another field's `control` (e.g. `product_id`, set by the
    // supplement picker when it fills `name`). A general marker rather than a
    // hardcoded key, so questionnaire-engine.ts's completeness helpers can
    // exclude it without knowing which key it is.
    writtenByControl: z.boolean().optional(),
});
// Deliberately no `label`/`helpText` here — copy (labels, intros, disclosure
// text, units) lives in @ferrivia/questionnaire-ui, not the contract. Do not
// populate `questionOptionSchema.label` in INSTRUMENT_DEFINITIONS either;
// that would create a second home for copy.
const questionCommonSchema = z.object({
    key: z.string(),
    required: z.boolean(),
    showIf: questionShowIfSchema.optional(),
    // A retired question is never asked again, but its stored answers still
    // render wherever present. It stays in the definition so its key, options
    // and copy keep resolving for old submissions and the Glide import. Not a
    // version bump: no stored answer becomes unreadable, and a bump would
    // strand every open v1 draft (saveAnswers rejects a version mismatch).
    retired: z.literal(true).optional(),
});
export const singleQuestionDefinitionSchema = questionCommonSchema.extend({
    repeating: z.literal(false),
    kind: answerKindSchema,
    options: z.array(questionOptionSchema).optional(),
    // value_number is `numeric` in Postgres, so integer-ness and bounds are
    // enforced here, not by the column.
    integer: z.boolean().optional(),
    min: z.number().optional(),
    max: z.number().optional(),
    // A rendering hint, not copy -- same reasoning as the group field's
    // `control`, and like it never a version bump. `long_text` renders a text
    // question as a tall textarea instead of the default one-line,
    // auto-growing one; `searchable_multi_choice` renders a multi_choice
    // question with a long option list as a search box over the options, the
    // selections shown as removable chips.
    control: z.enum(["long_text", "searchable_multi_choice"]).optional(),
});
// A question whose answer is a list of field records (e.g. one row per iron
// supplement the client has used) rather than a single value. Modelled as a
// discriminated variant, not an optional `fields?` on every question, so a
// plain question can't accidentally carry fields.
export const repeatingQuestionDefinitionSchema = questionCommonSchema.extend({
    repeating: z.literal(true),
    fields: z.array(questionGroupFieldSchema),
    // A rendering hint for the client's item card, not copy: the first key's
    // value is the card title, the rest its subtitle. Additive, so adding or
    // changing it is not a version bump.
    summaryFieldKeys: z.array(z.string()).optional(),
});
export const questionDefinitionSchema = z.discriminatedUnion("repeating", [
    singleQuestionDefinitionSchema,
    repeatingQuestionDefinitionSchema,
]);
// The hub's section list. Structure lives here (the contract), titles in
// @ferrivia/questionnaire-ui -- the same split questionCommonSchema's
// comment already draws between identity/gating and copy. Every question key
// appears in exactly one section (questionnaires.test.ts checks both
// directions).
export const instrumentSectionSchema = z.object({
    id: z.string(),
    questionKeys: z.array(z.string()).nonempty(),
});
function q(question) {
    // required: false everywhere in v1 — see the instrument table's decision 8.
    return { ...question, repeating: false, required: false };
}
function group(question) {
    return { ...question, repeating: true, required: false };
}
function field(f) {
    return { ...f, required: false };
}
// The single source of truth for the shared instrument. apps/api's
// INSTRUMENTS (modules/questionnaires/store.ts) is derived from this rather
// than being a second hardcoded list.
//
// Transcribed from the live Glide app (screenshots, 2026-09-09). Deliberate
// omissions, not oversights: no required questions in v1, no follow-up/"since your last report"
// instrument, `allergies_or_foods_avoided_y_n` not ported (no control exists
// for it).
// Iron infusions are a repeating group (`iron_infusion_items`, one item per
// infusion), added after v1 went live. `iron_infusion_details`, the original
// single free-text field, is kept as an optional notes field below it, so
// answers given before the group existed still render in the clinic.
// Not a version bump: sections add no question, change no key and change no
// showIf, so a v1 submission is still exactly as answerable. `background`
// (19 questions in the Glide "Page 1") is split into `background` (6:
// conditions and medication) and `menstruation_and_pregnancy` (13) -- too
// long for one page otherwise, and the deck's own section titles already
// separate them ("Kuukautiset ja raskaus").
const HEALTH_QUESTIONNAIRE_SECTIONS = [
    {
        id: "background",
        questionKeys: [
            "female_reproductive_stage",
            "medical_conditions",
            "medical_conditions_details",
            "medical_conditions_other",
            "medication",
            "medication_details",
        ],
    },
    {
        id: "menstruation_and_pregnancy",
        questionKeys: [
            "menstruation_regularity",
            "menstrual_cycle_consistency",
            "menstrual_cycle_length",
            "menstrual_cycle_shortest",
            "menstrual_cycle_longest",
            "menstruation_length",
            "menstruation_amount",
            "heavy_menstruation_signs",
            "contraceptives",
            "contraceptives_details",
            "contraceptives_other",
            "miscarriage",
            "no_of_miscarriages",
            "last_miscarriage",
            "child_birth",
            "no_of_children",
            "last_childbirth",
        ],
    },
    {
        id: "lifestyle",
        questionKeys: ["diet", "dairy", "coffee_tea", "allergies", "sleep", "exercise", "smoking", "alcohol_consumption"],
    },
    {
        id: "gut_health",
        questionKeys: [
            "bowel_movement",
            "stool_quality",
            "gut_symptoms",
            "avoiding_foods",
            "avoided_foods",
            "diagnosed_gut_conditions",
            "gut_conditions_details",
        ],
    },
    {
        id: "iron_supplements",
        questionKeys: ["currently_on_iron_supplement", "iron_supplements"],
    },
    {
        id: "other_supplements",
        questionKeys: ["other_supplements", "other_supplement_items"],
    },
    {
        id: "iron_infusions",
        questionKeys: ["iron_infusion", "iron_infusion_items", "iron_infusion_details"],
    },
    {
        id: "blood_donation",
        questionKeys: ["blood_donation", "blood_donation_details"],
    },
    {
        id: "additional_information",
        questionKeys: ["other_additional_information"],
    },
];
export const INSTRUMENT_DEFINITIONS = {
    health_questionnaire: {
        version: 1,
        sections: HEALTH_QUESTIONNAIRE_SECTIONS,
        questions: [
            // Page 1 — background
            q({
                key: "female_reproductive_stage",
                kind: "multi_choice",
                options: [
                    { id: "menstruating" },
                    { id: "planning_pregnancy" },
                    { id: "trying_to_conceive" },
                    { id: "fertility_treatment" },
                    { id: "pregnant" },
                    { id: "gave_birth_within_last_year" },
                    { id: "perimenopause" },
                    { id: "menopause" },
                    { id: "postmenopause" },
                ],
            }),
            q({ key: "medical_conditions", kind: "boolean" }),
            q({
                key: "medical_conditions_details",
                kind: "multi_choice",
                showIf: { key: "medical_conditions", equals: true },
                control: "searchable_multi_choice",
                // Alphabetical by fi label -- an editorial order, never a ranking or
                // a "common" section. Rendered as a searchable list, see `control`.
                options: [
                    { id: "addisons_disease" },
                    { id: "adenomyosis" },
                    { id: "adhd" },
                    { id: "anxiety" },
                    { id: "asthma" },
                    { id: "atopic_dermatitis" },
                    { id: "atrophic_gastritis" },
                    { id: "b12_deficiency" },
                    // Glide's own label had a typo ("Chronin tauti") — the disease is
                    // Crohn's; the fi copy fixes the label, this id is unaffected.
                    { id: "crohns_disease" },
                    { id: "diverticular_disease" },
                    { id: "endometriosis" },
                    { id: "epilepsy" },
                    { id: "atrial_fibrillation" },
                    { id: "fibromyalgia" },
                    { id: "folate_deficiency" },
                    { id: "ulcerative_colitis" },
                    { id: "helicobacter" },
                    { id: "hemochromatosis" },
                    { id: "bipolar_disorder" },
                    { id: "celiac_disease" },
                    { id: "copd" },
                    { id: "autoimmune_thyroiditis" },
                    { id: "hyperthyroidism" },
                    { id: "hypothyroidism" },
                    { id: "uterine_fibroids" },
                    { id: "hypertension" },
                    { id: "chronic_kidney_disease" },
                    { id: "lactose_intolerance" },
                    { id: "venous_thrombosis" },
                    { id: "restless_legs" },
                    { id: "bariatric_surgery" },
                    { id: "sle" },
                    { id: "peptic_ulcer" },
                    { id: "gastritis" },
                    { id: "liver_disease" },
                    { id: "depression" },
                    { id: "me_cfs" },
                    { id: "migraine" },
                    { id: "pcos" },
                    { id: "ms" },
                    { id: "gi_surgery" },
                    { id: "rheumatoid_arthritis" },
                    { id: "osteoporosis" },
                    { id: "hemorrhoids" },
                    { id: "long_covid" },
                    { id: "pmdd" },
                    { id: "pots" },
                    { id: "psoriasis" },
                    { id: "psoriatic_arthritis" },
                    { id: "gestational_diabetes" },
                    { id: "fatty_liver" },
                    { id: "iron_deficiency_anemia" },
                    { id: "gerd" },
                    { id: "rheumatic_disease" },
                    { id: "heavy_menstrual_bleeding" },
                    { id: "food_allergy" },
                    { id: "ankylosing_spondylitis" },
                    { id: "coronary_artery_disease" },
                    { id: "sickle_cell" },
                    { id: "sjogrens" },
                    { id: "heart_failure" },
                    { id: "eating_disorder" },
                    { id: "cancer" },
                    { id: "thalassemia" },
                    { id: "type_1_diabetes" },
                    { id: "type_2_diabetes" },
                    { id: "insomnia" },
                    { id: "sleep_apnea" },
                    { id: "bleeding_disorder" },
                    { id: "ibs" },
                ],
            }),
            q({ key: "medical_conditions_other", kind: "text", showIf: { key: "medical_conditions", equals: true } }),
            q({ key: "medication", kind: "boolean" }),
            q({ key: "medication_details", kind: "text", showIf: { key: "medication", equals: true } }),
            // Retired 2026-11-03: vague, and redundant with the cycle block below.
            // "No periods" moved to menstrual_cycle_consistency. Kept for stored
            // answers and the Glide import, whose questionnaires have only this.
            q({
                key: "menstruation_regularity",
                kind: "choice",
                retired: true,
                options: [
                    { id: "regular" },
                    { id: "somewhat_regular" },
                    { id: "somewhat_irregular" },
                    { id: "irregular" },
                    { id: "no_periods" },
                ],
            }),
            // Not a version bump: optional and additive. min/max are input sanity
            // bounds only, deliberately not clinical cut-points for cycle length.
            // The showIf is a legacy gate: it hides the block for a stored
            // regularity answer of no_periods and passes when unanswered, which is
            // every submission since regularity was retired.
            q({
                key: "menstrual_cycle_consistency",
                kind: "choice",
                showIf: { key: "menstruation_regularity", notEquals: "no_periods" },
                options: [{ id: "about_same" }, { id: "varies" }, { id: "dont_know" }, { id: "no_periods" }],
            }),
            q({
                key: "menstrual_cycle_length",
                kind: "number",
                integer: true,
                min: 1,
                max: 365,
                showIf: { key: "menstrual_cycle_consistency", equals: "about_same" },
            }),
            q({
                key: "menstrual_cycle_shortest",
                kind: "number",
                integer: true,
                min: 1,
                max: 365,
                showIf: { key: "menstrual_cycle_consistency", equals: "varies" },
            }),
            q({
                key: "menstrual_cycle_longest",
                kind: "number",
                integer: true,
                min: 1,
                max: 365,
                showIf: { key: "menstrual_cycle_consistency", equals: "varies" },
            }),
            // These three gate on consistency; isReachable is transitive, so a
            // legacy regularity no_periods still hides them through consistency.
            q({
                key: "menstruation_length",
                kind: "number",
                integer: true,
                showIf: { key: "menstrual_cycle_consistency", notEquals: "no_periods" },
            }),
            // Column renamed from Glide's `menstuation_ammount` [sic] — the typo is
            // not ported. `question_key` is free-form application data (032), so a
            // future backfill maps the old column name to this key rather than
            // reusing it verbatim.
            q({
                key: "menstruation_amount",
                kind: "choice",
                showIf: { key: "menstrual_cycle_consistency", notEquals: "no_periods" },
                options: [
                    { id: "light" },
                    { id: "moderate" },
                    { id: "heavy" },
                    { id: "very_heavy" },
                ],
            }),
            q({
                key: "heavy_menstruation_signs",
                kind: "boolean",
                showIf: { key: "menstrual_cycle_consistency", notEquals: "no_periods" },
            }),
            q({ key: "contraceptives", kind: "boolean" }),
            q({
                key: "contraceptives_details",
                kind: "multi_choice",
                showIf: { key: "contraceptives", equals: true },
                options: [
                    { id: "combined_pill" },
                    { id: "minipill" },
                    { id: "hormonal_iud" },
                    { id: "copper_iud" },
                    { id: "vaginal_ring" },
                    { id: "contraceptive_patch" },
                    { id: "contraceptive_implant" },
                    { id: "contraceptive_injection" },
                    { id: "other" },
                ],
            }),
            q({
                key: "contraceptives_other",
                kind: "text",
                showIf: { key: "contraceptives_details", includesAny: ["other"] },
            }),
            q({ key: "miscarriage", kind: "boolean" }),
            q({ key: "no_of_miscarriages", kind: "number", integer: true, showIf: { key: "miscarriage", equals: true } }),
            q({ key: "last_miscarriage", kind: "date", showIf: { key: "miscarriage", equals: true } }),
            q({ key: "child_birth", kind: "boolean" }),
            q({ key: "no_of_children", kind: "number", integer: true, showIf: { key: "child_birth", equals: true } }),
            q({ key: "last_childbirth", kind: "date", showIf: { key: "child_birth", equals: true } }),
            // Page 2 — lifestyle
            q({
                key: "diet",
                kind: "multi_choice",
                options: [
                    { id: "omnivore" },
                    { id: "no_red_meat" },
                    { id: "plant_based" },
                    { id: "pescatarian" },
                    { id: "vegetarian" },
                    { id: "vegan" },
                    { id: "dairy_free" },
                    { id: "gluten_free" },
                ],
            }),
            q({ key: "dairy", kind: "number", integer: true }),
            q({ key: "coffee_tea", kind: "number", integer: true }),
            q({ key: "allergies", kind: "text" }),
            q({ key: "sleep", kind: "number", integer: true }),
            q({ key: "exercise", kind: "number", integer: true }),
            q({ key: "smoking", kind: "boolean" }),
            q({ key: "alcohol_consumption", kind: "number", integer: true }),
            // Page 3 — gut health
            q({
                key: "bowel_movement",
                kind: "choice",
                options: [
                    { id: "more_than_3_times_daily" },
                    { id: "1_to_3_times_daily" },
                    { id: "0_to_1_times_daily" },
                    { id: "not_every_day" },
                    { id: "a_few_times_weekly" },
                    { id: "less_than_once_weekly" },
                ],
            }),
            q({
                key: "stool_quality",
                kind: "multi_choice",
                options: [
                    { id: "black" },
                    { id: "pale_or_yellowish" },
                    { id: "red_or_bloody" },
                    { id: "hard_or_lumpy" },
                    { id: "soft_or_loose" },
                    { id: "watery" },
                    { id: "floating" },
                    { id: "oily_or_greasy" },
                ],
            }),
            q({
                key: "gut_symptoms",
                kind: "multi_choice",
                options: [
                    { id: "diarrhea" },
                    { id: "loose_stool" },
                    { id: "constipation" },
                    { id: "bloating" },
                    { id: "burping" },
                    { id: "flatulence" },
                    { id: "abdominal_pain" },
                    { id: "heartburn" },
                ],
            }),
            q({ key: "avoiding_foods", kind: "boolean" }),
            q({ key: "avoided_foods", kind: "text", showIf: { key: "avoiding_foods", equals: true } }),
            q({ key: "diagnosed_gut_conditions", kind: "boolean" }),
            q({
                key: "gut_conditions_details",
                kind: "text",
                showIf: { key: "diagnosed_gut_conditions", equals: true },
            }),
            // Page 4 — iron supplements
            // Past tense in Glide's copy ("Oletko käyttänyt") reads as "ever used"
            // rather than "currently on", but the key is kept as
            // `currently_on_iron_supplement` per the plan's stated default; the
            // question actually gates a list of iron supplements each with their
            // own started_at/still_using, which fits "have you ever used any" more
            // than "are you currently on one".
            q({ key: "currently_on_iron_supplement", kind: "boolean" }),
            group({
                key: "iron_supplements",
                showIf: { key: "currently_on_iron_supplement", equals: true },
                summaryFieldKeys: ["name", "daily_iron_dose"],
                fields: [
                    // Glide's separate "Jokin muu, mikä?" field is folded into `name`
                    // rather than ported as a second field — redundant once `name` is
                    // free text (decision 9: no product picker in v1).
                    field({ key: "name", kind: "text", control: "supplement_picker", catalogCategory: "iron" }),
                    // Written by the picker alongside `name`, from the curated catalog
                    // (037_supplement_catalog.sql) -- never rendered or answered on
                    // its own, see `writtenByControl`.
                    // Not a version bump: the field is optional and additive, so every
                    // v1 submission stays exactly as answerable. A bump would also
                    // strand them -- saveAnswers rejects any submission whose stored
                    // instrument_version no longer equals the definition's.
                    field({ key: "product_id", kind: "text", writtenByControl: true }),
                    field({ key: "daily_iron_dose", kind: "number", integer: true }),
                    field({
                        key: "frequency",
                        kind: "choice",
                        options: [
                            { id: "occasionally" },
                            { id: "every_other_day" },
                            { id: "once_daily" },
                            { id: "twice_daily" },
                        ],
                    }),
                    // multi_choice so one product can be taken at several times of
                    // day. Not a version bump: the store and answerText dispatch on
                    // each row's stored kind, so a legacy `choice` row stays readable.
                    field({
                        key: "timing",
                        kind: "multi_choice",
                        options: [{ id: "morning" }, { id: "evening" }, { id: "midday" }],
                    }),
                    field({
                        key: "with_meals",
                        kind: "choice",
                        options: [{ id: "empty_stomach" }, { id: "with_food" }],
                    }),
                    field({ key: "started_at", kind: "date" }),
                    field({ key: "still_using", kind: "boolean" }),
                    field({ key: "stopped_at", kind: "date", showIf: { key: "still_using", equals: false } }),
                    field({ key: "stop_reason", kind: "text", showIf: { key: "still_using", equals: false } }),
                    // Wording is tense-neutral in copy regardless of `still_using`
                    // (Glide varies "Aiheuttiko"/"Aiheuttaako" by tense; the platform
                    // does not vary copy by an answer — see root CLAUDE.md).
                    field({ key: "side_effects", kind: "boolean" }),
                    field({ key: "side_effects_detail", kind: "text", showIf: { key: "side_effects", equals: true } }),
                    // Photos of the label, stored and shown to the care team as
                    // uploaded -- never read by software. Not a version bump: optional
                    // and additive, same as `product_id`.
                    field({ key: "photos", kind: "text", control: "photo_upload" }),
                ],
            }),
            // Page 5 — other supplements
            q({ key: "other_supplements", kind: "boolean" }),
            group({
                key: "other_supplement_items",
                showIf: { key: "other_supplements", equals: true },
                summaryFieldKeys: ["name"],
                // Glide's image upload came back as `photos` (originally left out,
                // decision 5): a complement to the picker and `nutrients`, not a
                // replacement. Stored and shown as uploaded, never read by software.
                fields: [
                    field({ key: "name", kind: "text", control: "supplement_picker", catalogCategory: "other" }),
                    field({ key: "product_id", kind: "text", writtenByControl: true }),
                    field({ key: "nutrients", kind: "text" }),
                    field({ key: "photos", kind: "text", control: "photo_upload" }),
                ],
            }),
            // Pages 6-7
            q({ key: "iron_infusion", kind: "boolean" }),
            // Not a version bump: the group is additive, so a v1 submission
            // without it is still exactly as answerable. Product options are an
            // editorial order, never a ranking; brand names live in
            // @ferrivia/questionnaire-ui. Fercarbos sits beside Ferinject as the
            // same molecule (ferric carboxymaltose); Injectafer is its US brand.
            group({
                key: "iron_infusion_items",
                showIf: { key: "iron_infusion", equals: true },
                summaryFieldKeys: ["product", "infusion_date", "dose_mg"],
                fields: [
                    field({ key: "infusion_date", kind: "date" }),
                    field({ key: "place", kind: "text" }),
                    field({
                        key: "product",
                        kind: "choice",
                        control: "select",
                        options: [
                            { id: "ferinject", markets: ["fi"] },
                            { id: "fercarbos", markets: ["fi"] },
                            { id: "monofer", markets: ["fi"] },
                            { id: "injectafer", markets: ["us"] },
                            { id: "feraheme", markets: ["us"] },
                            { id: "venofer" },
                            { id: "cosmofer", markets: ["fi"] },
                            { id: "other_or_unknown" },
                        ],
                    }),
                    field({ key: "product_other", kind: "text", showIf: { key: "product", equals: "other_or_unknown" } }),
                    field({ key: "dose_mg", kind: "number", integer: true, min: 0 }),
                ],
            }),
            q({ key: "iron_infusion_details", kind: "text", showIf: { key: "iron_infusion", equals: true } }),
            // Blood donation. Not a version bump: additive, a v1 submission without
            // these keys is still exactly as answerable.
            q({ key: "blood_donation", kind: "boolean" }),
            q({ key: "blood_donation_details", kind: "text", showIf: { key: "blood_donation", equals: true } }),
            // control is a rendering hint, so adding it is not a version bump.
            q({ key: "other_additional_information", kind: "text", control: "long_text" }),
        ],
    },
};
// The wire shape of one answer. Discriminated on kind so a client can only
// ever send the value column that matches — the same one-value-per-row
// invariant the DB CHECK (032) enforces server-side.
export const answerValueSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("text"), value: z.string() }),
    z.object({ kind: z.literal("number"), value: z.number() }),
    z.object({ kind: z.literal("boolean"), value: z.boolean() }),
    z.object({ kind: z.literal("date"), value: z.string() }),
    z.object({ kind: z.literal("choice"), value: z.string() }),
    z.object({ kind: z.literal("multi_choice"), value: z.array(z.string()) }),
]);
// Just-in-time consent, captured at questionnaire open (036). documentVersion
// is client-supplied here, unlike onboarding's server-stamped
// CONSENT_DOCUMENTS -- the consent copy that would let the server stamp it
// lives in @ferrivia/questionnaire-ui, a later phase of this stack.
export const questionnaireConsentInputSchema = z.object({
    granted: z.boolean(),
    documentVersion: z.string(),
});
export const questionnaireStartRequestSchema = z.object({
    consent: questionnaireConsentInputSchema.optional(),
});
export const questionnaireAnswerInputSchema = z.object({
    questionKey: z.string(),
    value: answerValueSchema,
});
export const questionnaireItemFieldInputSchema = z.object({
    fieldKey: z.string(),
    value: answerValueSchema,
});
export const questionnaireItemInputSchema = z.object({
    questionKey: z.string(),
    itemIndex: z.number().int().nonnegative(),
    fields: z.array(questionnaireItemFieldInputSchema),
});
export const saveAnswersRequestSchema = z.object({
    answers: z.array(questionnaireAnswerInputSchema).default([]),
    items: z.array(questionnaireItemInputSchema).optional(),
});
// A count, never the values back -- the same "the software does not
// interpret" boundary as completeness() below.
export const saveAnswersResponseSchema = questionnaireStateSchema.extend({
    answered: z.number().int().nonnegative(),
});
// The client's own read of its submission -- never an auditedRead target,
// same reasoning as symptom-tracker.md: a client reading only its own data
// isn't the clinic-crosses-a-record-boundary case that rail exists for.
// The clinic app now reads this same shape too, via the
// GET /clinic/patients/:id/questionnaire route -- that read is an
// auditedRead (patient_questionnaire, 033); the client's own GET above still
// is not.
export const questionnaireAnswerSchema = z.object({
    questionKey: z.string(),
    value: answerValueSchema,
});
export const questionnaireItemSchema = z.object({
    questionKey: z.string(),
    itemIndex: z.number().int().nonnegative(),
    fields: z.array(z.object({ fieldKey: z.string(), value: answerValueSchema })),
});
// Redacted rows (GDPR erasure -- all value_* columns null) are dropped by the
// store before this shape is built, never surfaced as an empty-valued
// answer: absence of an entry is what "unanswered" already means on the
// wire, and a redacted answer must read exactly the same way.
export const questionnaireDetailSchema = questionnaireStateSchema.extend({
    consentGranted: z.boolean(),
    answers: z.array(questionnaireAnswerSchema),
    items: z.array(questionnaireItemSchema),
    lastSavedAt: z.string().nullable(),
    submittedAt: z.string().nullable(),
    // True once the open submission has a full symptom check-in recorded since
    // it started -- the questionnaire's required symptom step. Always false
    // without an open submission.
    symptomCheckDone: z.boolean(),
});
// The client's own read only, never the clinic's: `prefill` is a suggestion
// the client sees preselected (today, the onboarding life stage for
// `female_reproductive_stage`), not a saved answer. It is stored only once
// the client's own flush writes it, so the clinic shape stays unchanged.
// Empty once the key has any saved row or the submission is submitted.
export const clientQuestionnaireDetailSchema = questionnaireDetailSchema.extend({
    prefill: z.array(questionnaireAnswerSchema),
});
// Takes which required questions have been answered, never what was
// answered — completeness is a count, not a read of any value.
export function completeness(instrument, answeredKeys) {
    const requiredKeys = INSTRUMENT_DEFINITIONS[instrument].questions
        .filter((question) => question.required)
        .map((question) => question.key);
    const answeredSet = new Set(answeredKeys);
    const answered = requiredKeys.filter((key) => answeredSet.has(key)).length;
    return { answered, required: requiredKeys.length, complete: answered === requiredKeys.length };
}
